import React, { useCallback, useEffect, useState } from "react";
import { db, storage } from "../config/firebaseConfig";
import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  Timestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { getBlob, getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { useLocation } from "react-router-dom";
import {
  getAllowedRoles,
  hasPermission,
  PERMISSIONS,
} from "../utils/roleAccess";
import { IMMICH_BASE_URL } from "../config/immichConfig";
import {
  buildImmichPersonName as buildImmichPersonNameFromImmichService,
  syncImmichPersonByUid as syncImmichPersonByUidFromImmichService,
} from "../services/immichService";

import toast from "react-hot-toast";

const EMPTY_FORM_DATA = {
  name: "",
  className: "",
  rollNo: "",
  qrImageUrl: "",
  activeqr: false,
  father: "",
  mother: "",
  phone: "",
  altPhone: "",
  address: "",
  uid: "",
  transportation: "Self",
  busRouteNo: "",
  busNo: "",
  driverName: "",
  conductorName: "",
  busPhone: "",
  studentAadhaar: "",
  fatherAadhaar: "",
  motherAadhaar: "",
  dob: "",
  admissionDate: "",
  guardianName: "",
  guardianRelation: "",
  guardianPhone: "",
  guardianAddress: "",
};

const CLASS_OPTIONS = [
  "Nursery",
  "Nursery_A",
  "Nursery_B",
  "LKG",
  "UKG",
  "Class 1",
  "Class 2",
  "Class 3",
  "Class 4",
  "Class 5",
  "Class 6",
  "Class 7",
  "Class 8",
];

function buildFreshFormData(uid = "") {
  return {
    ...EMPTY_FORM_DATA,
    uid,
  };
}

const IMMICH_ASSET_UPLOAD_URL = `${IMMICH_BASE_URL}/assets`;
const IMMICH_API_KEY = (process.env.REACT_APP_IMMICH_API_KEY || "").trim();
const IMMICH_UPLOAD_TIMEOUT_MS = Number(
  process.env.REACT_APP_IMMICH_UPLOAD_TIMEOUT_MS || 180000,
);
const IMMICH_DEVICE_LABEL =
  process.env.REACT_APP_IMMICH_DEVICE_ID || "rising-stars-web-admin";

const fetchWithTimeout = async (url, options = {}, timeoutMs = 45000) => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(url, {
      ...options,
      signal: controller.signal,
    });
  } finally {
    clearTimeout(timer);
  }
};

const extractResponsePayload = async (response) => {
  const textBody = await response.text();
  let jsonBody = null;
  try {
    jsonBody = textBody ? JSON.parse(textBody) : null;
  } catch {
    jsonBody = null;
  }

  return { textBody, jsonBody };
};

const getImmichNetworkError = (error) => {
  if (error?.name === "AbortError") {
    return `Request timed out after ${IMMICH_UPLOAD_TIMEOUT_MS}ms`;
  }

  const raw = String(error?.message || "");
  if (raw.toLowerCase().includes("failed to fetch")) {
    return "Network/CORS/Proxy issue (Failed to fetch)";
  }

  return raw || "Unknown network error";
};

const buildDeviceAssetId = () => {
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.randomUUID === "function"
  ) {
    return crypto.randomUUID();
  }
  return `asset-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

const buildImmichUploadPayload = (file) => {
  const fileTimestamp = new Date(
    file?.lastModified || Date.now(),
  ).toISOString();
  const formData = new FormData();

  formData.append("deviceAssetId", buildDeviceAssetId());
  formData.append("deviceId", IMMICH_DEVICE_LABEL);
  formData.append("fileCreatedAt", fileTimestamp);
  formData.append("fileModifiedAt", fileTimestamp);
  formData.append("assetData", file, file?.name || "student-photo.jpg");

  return formData;
};

const parseImmichErrorMessage = (status, textBody, defaultMessage) => {
  const normalizedText = String(textBody || "").trim();
  if (!normalizedText) {
    return `${defaultMessage} (${status})`;
  }

  const shortText =
    normalizedText.length > 220
      ? `${normalizedText.slice(0, 220)}...`
      : normalizedText;

  return `${defaultMessage} (${status}): ${shortText}`;
};

const emitImmichLog = (onLog, level, role, message, details = null) => {
  if (typeof onLog !== "function") {
    return;
  }

  onLog({
    level,
    role,
    message,
    details,
    timestamp: new Date().toISOString(),
  });
};

async function uploadImmichAssetForRole({
  uidValue,
  role = "student",
  file,
  onLog = null,
}) {
  if (!file) {
    emitImmichLog(
      onLog,
      "warning",
      role,
      `Skipped ${role} asset upload: no photo selected`,
    );
    return {
      status: "skipped",
      message: `No ${role} photo selected for Immich sync.`,
    };
  }

  if (!IMMICH_API_KEY) {
    emitImmichLog(
      onLog,
      "warning",
      role,
      `Skipped ${role} asset upload: missing REACT_APP_IMMICH_API_KEY`,
    );
    return {
      status: "skipped",
      message: "Missing REACT_APP_IMMICH_API_KEY, skipped Immich sync.",
    };
  }

  emitImmichLog(onLog, "info", role, `Uploading ${role} asset to Immich`, {
    endpoint: IMMICH_ASSET_UPLOAD_URL,
    fileName: file?.name || "",
    fileType: file?.type || "",
    uid: uidValue,
  });

  try {
    const uploadRes = await fetchWithTimeout(
      IMMICH_ASSET_UPLOAD_URL,
      {
        method: "POST",
        headers: {
          Accept: "application/json",
          "x-api-key": IMMICH_API_KEY,
        },
        body: buildImmichUploadPayload(file),
      },
      IMMICH_UPLOAD_TIMEOUT_MS,
    );

    const { textBody, jsonBody } = await extractResponsePayload(uploadRes);

    emitImmichLog(onLog, "info", role, `${role} asset upload response`, {
      status: uploadRes.status,
      statusText: uploadRes.statusText,
      url: uploadRes.url,
    });

    if (!uploadRes.ok) {
      throw new Error(
        parseImmichErrorMessage(
          uploadRes.status,
          textBody,
          "Immich asset upload failed",
        ),
      );
    }

    const assetId = jsonBody?.id || jsonBody?.assetId || jsonBody?.asset?.id;

    if (!assetId) {
      throw new Error("Immich upload did not return an asset id.");
    }

    emitImmichLog(onLog, "success", role, `${role} asset uploaded`, {
      assetId,
      fileName: file?.name || "",
    });

    return {
      status: "success",
      assetId,
      role,
    };
  } catch (error) {
    const message = getImmichNetworkError(error) || error.message;
    emitImmichLog(onLog, "error", role, `${role} asset upload failed`, {
      message,
    });

    return {
      status: "failed",
      message,
      role,
    };
  }
}

const inferImageFileExtension = (contentType = "") => {
  const normalizedType = String(contentType || "").toLowerCase().trim();

  if (normalizedType.includes("png")) {
    return ".png";
  }

  if (normalizedType.includes("webp")) {
    return ".webp";
  }

  if (normalizedType.includes("gif")) {
    return ".gif";
  }

  if (normalizedType.includes("bmp")) {
    return ".bmp";
  }

  if (normalizedType.includes("tiff")) {
    return ".tif";
  }

  if (normalizedType.includes("heic")) {
    return ".heic";
  }

  if (normalizedType.includes("heif")) {
    return ".heif";
  }

  return ".jpg";
};

const sanitizeFileName = (value) => {
  const raw = String(value || "");
  const invalidCharSet = new Set(["<", ">", ":", '"', "/", "\\", "|", "?", "*"]);
  let output = "";

  for (const char of raw) {
    const codePoint = char.charCodeAt(0);

    if (invalidCharSet.has(char)) {
      output += "_";
      continue;
    }

    if (codePoint <= 32) {
      output += "_";
      continue;
    }

    output += char;
  }

  return output.replace(/_+/g, "_").replace(/^_+|_+$/g, "");
};

const getFileNameFromUrl = (url, role = "student", contentType = "") => {
  const fallbackBase = `${role}-photo`;
  let candidate = fallbackBase;

  try {
    const parsedUrl = new URL(String(url || ""));
    const segments = parsedUrl.pathname.split("/").filter(Boolean);
    const lastSegment = segments[segments.length - 1] || "";

    candidate = lastSegment ? decodeURIComponent(lastSegment) : fallbackBase;
  } catch {
    candidate = fallbackBase;
  }

  const baseName = String(candidate)
    .split("/")
    .filter(Boolean)
    .slice(-1)[0];
  const sanitizedBase = sanitizeFileName(baseName || fallbackBase) || fallbackBase;

  if (/\.[a-z0-9]{2,6}$/i.test(sanitizedBase)) {
    return sanitizedBase;
  }

  return `${sanitizedBase}${inferImageFileExtension(contentType)}`;
};

const parseFirebaseStoragePathFromUrl = (url) => {
  const raw = String(url || "").trim();

  if (!raw) {
    return "";
  }

  if (raw.startsWith("gs://")) {
    const withoutScheme = raw.slice("gs://".length);
    const segments = withoutScheme.split("/").filter(Boolean);

    if (segments.length >= 2) {
      return decodeURIComponent(segments.slice(1).join("/"));
    }
  }

  try {
    const parsedUrl = new URL(raw);
    const host = String(parsedUrl.hostname || "");
    const isFirebaseStorageHost = host.includes("firebasestorage.googleapis.com");
    const isGcsHost = host === "storage.googleapis.com";

    if (!isFirebaseStorageHost && !isGcsHost) {
      return "";
    }

    const objectPathMarker = "/o/";
    const markerIndex = parsedUrl.pathname.indexOf(objectPathMarker);

    if (markerIndex !== -1) {
      const encodedObjectPath = parsedUrl.pathname.slice(
        markerIndex + objectPathMarker.length,
      );

      return decodeURIComponent(encodedObjectPath || "");
    }

    if (isGcsHost) {
      const segments = parsedUrl.pathname.split("/").filter(Boolean);
      if (segments.length >= 2) {
        return decodeURIComponent(segments.slice(1).join("/"));
      }
    }

    return "";
  } catch {
    return "";
  }
};

async function getImageBlobForImmich(imageUrl, role = "student", onLog = null) {
  const normalizedUrl = String(imageUrl || "").trim();
  const storagePath = parseFirebaseStoragePathFromUrl(normalizedUrl);

  if (storagePath) {
    try {
      emitImmichLog(
        onLog,
        "info",
        role,
        "Fetching image via Firebase Storage SDK",
        {
          storagePath,
        },
      );

      return await getBlob(ref(storage, storagePath));
    } catch (error) {
      emitImmichLog(
        onLog,
        "warning",
        role,
        "Firebase SDK image fetch failed, falling back to URL fetch",
        {
          storagePath,
          message: error?.message || "Unknown error",
        },
      );
    }
  }

  const response = await fetchWithTimeout(
    normalizedUrl,
    {
      method: "GET",
    },
    IMMICH_UPLOAD_TIMEOUT_MS,
  );

  if (!response.ok) {
    throw new Error(
      parseImmichErrorMessage(
        response.status,
        "",
        `Failed to fetch ${role} image URL`,
      ),
    );
  }

  return response.blob();
}

async function fetchImageFileFromUrlForImmich(
  imageUrl,
  role = "student",
  onLog = null,
) {
  const normalizedUrl = String(imageUrl || "").trim();

  if (!normalizedUrl) {
    return null;
  }

  emitImmichLog(onLog, "info", role, "Fetching existing image URL for Immich", {
    imageUrl: normalizedUrl,
  });

  const blob = await getImageBlobForImmich(normalizedUrl, role, onLog);
  const fileType = blob?.type || "image/jpeg";
  const fileName = getFileNameFromUrl(normalizedUrl, role, fileType);

  return new File([blob], fileName, {
    type: fileType,
    lastModified: Date.now(),
  });
}

async function syncImmichPersonForUid(
  uidValue,
  role = "student",
  onLog = null,
) {
  if (!IMMICH_API_KEY) {
    emitImmichLog(
      onLog,
      "warning",
      role,
      "Skipped createPerson: missing REACT_APP_IMMICH_API_KEY",
    );
    return {
      status: "skipped",
      message: "Missing REACT_APP_IMMICH_API_KEY, skipped Immich sync.",
    };
  }

  try {
    const personResult = await createImmichPersonByUid(uidValue, role, onLog);

    return {
      status: "success",
      personId: personResult.personId,
      personName:
        personResult.personName || buildImmichPersonName(uidValue, role),
      personCreateSkipped: Boolean(personResult.personCreateSkipped),
      personCreateUrl: personResult.personCreateUrl,
      role,
    };
  } catch (error) {
    const message = getImmichNetworkError(error) || error.message;
    emitImmichLog(onLog, "error", role, "createPerson failed", {
      personName: buildImmichPersonName(uidValue, role),
      message,
    });

    return {
      status: "failed",
      message,
      personName: buildImmichPersonName(uidValue, role),
      role,
    };
  }
}

export default function StudentDetailsForm({
  initialUid = "",
  initialStudentRecord = null,
  activateQrOnSubmit = false,
  embedded = false,
}) {
  const location = useLocation();
  const uidFromQuery = String(
    new URLSearchParams(location.search).get("uid") || "",
  ).trim();
  const resolvedInitialUid = String(initialUid || "").trim() || uidFromQuery;
  const [formData, setFormData] = useState(buildFreshFormData());
  const [studentUid, setStudentUid] = useState("");
  const [studentImage, setStudentImage] = useState(null);
  const [studentPreview, setStudentPreview] = useState("");
  const [fatherImage, setFatherImage] = useState(null);
  const [fatherPreview, setFatherPreview] = useState("");
  const [motherImage, setMotherImage] = useState(null);
  const [motherPreview, setMotherPreview] = useState("");
  const [guardianImage, setGuardianImage] = useState(null);
  const [guardianPreview, setGuardianPreview] = useState("");
  const [saving, setSaving] = useState(false);
  const [searching, setSearching] = useState(false);
  const [canManageOnboarding, setCanManageOnboarding] = useState(false);
  const [user, setUser] = useState(null);
  const [, setImmichLogs] = useState([]);
  const resetUploads = useCallback(() => {
    setStudentImage(null);
    setFatherImage(null);
    setMotherImage(null);
    setGuardianImage(null);
  }, []);

  const resetPreviews = useCallback(() => {
    setStudentPreview("");
    setFatherPreview("");
    setMotherPreview("");
    setGuardianPreview("");
  }, []);

  function handleImagePreview(file, setPreview) {
    if (!file) {
      setPreview("");
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => setPreview(reader.result);
    reader.readAsDataURL(file);
  }

  function handleStudentImageChange(event) {
    const file = event.target.files[0];
    setStudentImage(file || null);
    handleImagePreview(file, setStudentPreview);
  }

  function handleFatherImageChange(event) {
    const file = event.target.files[0];
    setFatherImage(file || null);
    handleImagePreview(file, setFatherPreview);
  }

  function handleMotherImageChange(event) {
    const file = event.target.files[0];
    setMotherImage(file || null);
    handleImagePreview(file, setMotherPreview);
  }

  function handleGuardianImageChange(event) {
    const file = event.target.files[0];
    setGuardianImage(file || null);
    handleImagePreview(file, setGuardianPreview);
  }

  function handleChange(event) {
    const { name, value } = event.target;
    setFormData((currentValue) => ({
      ...currentValue,
      [name]: value,
    }));
  }

  function addImmichLog(entry) {
    setImmichLogs((currentValue) => [
      ...currentValue.slice(-119),
      {
        ...entry,
        id:
          typeof crypto !== "undefined" &&
          typeof crypto.randomUUID === "function"
            ? crypto.randomUUID()
            : `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      },
    ]);
  }

  function handleUidInputChange(event) {
    const uidValue = event.target.value.trim();
    setStudentUid(uidValue);
    setFormData((currentValue) => ({
      ...currentValue,
      uid: uidValue,
    }));
  }

  function formatToDDMMYYYY(value) {
    if (!value) {
      return "";
    }

    const parts = value.split("-");
    if (parts.length === 3) {
      const [year, month, day] = parts;
      return `${day}/${month}/${year}`;
    }

    const dateValue = new Date(value);

    if (Number.isNaN(dateValue.getTime())) {
      return "";
    }

    const day = String(dateValue.getDate()).padStart(2, "0");
    const month = String(dateValue.getMonth() + 1).padStart(2, "0");
    const year = dateValue.getFullYear();

    return `${day}/${month}/${year}`;
  }

  function handleDateChange(event) {
    const { name, value } = event.target;
    setFormData((currentValue) => ({
      ...currentValue,
      [name]: value,
      [`${name}Formatted`]: formatToDDMMYYYY(value),
    }));
  }

  const populateStudentForm = useCallback(
    (studentData, uidValue) => {
      setFormData({
        ...buildFreshFormData(uidValue),
        ...studentData,
        uid: uidValue,
      });
      setStudentUid(uidValue);
      setStudentPreview(studentData.studentImageUrl || "");
      setFatherPreview(studentData.fatherImageUrl || "");
      setMotherPreview(studentData.motherImageUrl || "");
      setGuardianPreview(studentData.guardianImageUrl || "");
      resetUploads();
    },
    [resetUploads],
  );

  const searchStudentByUid = useCallback(
    async (uidValue) => {
      const normalizedUid = String(uidValue || "").trim();

      if (!normalizedUid || normalizedUid.length !== 6) {
        alert("Enter a valid 6-digit Student ID to search");
        return false;
      }

      setSearching(true);

      try {
        const studentQuery = query(
          collection(db, "students"),
          where("uid", "==", normalizedUid),
        );
        const snapshot = await getDocs(studentQuery);

        if (!snapshot.empty) {
          const studentData = snapshot.docs[0].data();
          populateStudentForm(studentData, normalizedUid);
          return true;
        }

        setFormData(buildFreshFormData(normalizedUid));
        setStudentUid(normalizedUid);
        resetUploads();
        resetPreviews();
        return false;
      } catch (error) {
        toast.error(`Error searching student: ${error.message}`);
        return false;
      } finally {
        setSearching(false);
      }
    },
    [populateStudentForm, resetPreviews, resetUploads],
  );

  async function uploadSelectedImages(uidValue, onLog = null) {
    const uploads = [
      {
        file: studentImage,
        imageUrl: formData.studentImageUrl || "",
        path: `students/${uidValue}-student`,
        targetKey: "studentImageUrl",
        role: "student",
      },
      {
        file: fatherImage,
        imageUrl: formData.fatherImageUrl || "",
        path: `students/${uidValue}-father`,
        targetKey: "fatherImageUrl",
        role: "father",
      },
      {
        file: motherImage,
        imageUrl: formData.motherImageUrl || "",
        path: `students/${uidValue}-mother`,
        targetKey: "motherImageUrl",
        role: "mother",
      },
      {
        file: guardianImage,
        imageUrl: formData.guardianImageUrl || "",
        path: `students/${uidValue}-guardian`,
        targetKey: "guardianImageUrl",
        role: "guardian",
      },
    ];

    const imageUrls = {};
    for (const item of uploads) {
      if (!item.file) {
        continue;
      }

      const imageReference = ref(storage, item.path);
      await uploadBytes(imageReference, item.file);
      imageUrls[item.targetKey] = await getDownloadURL(imageReference);
    }

    const roleConfigs = [
      { role: "student", file: studentImage, displayName: "" },
      { role: "father", file: fatherImage, displayName: formData.father },
      { role: "mother", file: motherImage, displayName: formData.mother },
      {
        role: "guardian",
        file: guardianImage,
        displayName: formData.guardianName,
      },
    ];

    const readImageDimensions = (file) => {
      if (!file) {
        return Promise.resolve(null);
      }

      return new Promise((resolve, reject) => {
        const image = new Image();
        const objectUrl = URL.createObjectURL(file);

        image.onload = () => {
          URL.revokeObjectURL(objectUrl);
          resolve({
            imageWidth: image.naturalWidth || image.width || 0,
            imageHeight: image.naturalHeight || image.height || 0,
          });
        };

        image.onerror = () => {
          URL.revokeObjectURL(objectUrl);
          reject(new Error("Unable to read image dimensions for Immich sync."));
        };

        image.src = objectUrl;
      });
    };

    emitImmichLog(
      onLog,
      "info",
      "system",
      "Immich phase 1: Uploading assets for student and relatives",
      {
        roles: roleConfigs.map((item) => item.role),
      },
    );

    const assetResults = await Promise.all(
      roleConfigs.map(async ({ role, file }) => {
        const persistedImageUrl =
          imageUrls[`${role}ImageUrl`] || formData[`${role}ImageUrl`] || "";

        let fileForImmich = file;
        let source = "selected";

        if (!fileForImmich && persistedImageUrl) {
          try {
            fileForImmich = await fetchImageFileFromUrlForImmich(
              persistedImageUrl,
              role,
              onLog,
            );
            source = "database-url";
          } catch (error) {
            emitImmichLog(
              onLog,
              "error",
              role,
              `Failed to use existing ${role} image URL for Immich`,
              {
                imageUrl: persistedImageUrl,
                message: error?.message || "Unknown error",
              },
            );
          }
        }

        const result = await uploadImmichAssetForRole({
          uidValue,
          role,
          file: fileForImmich,
          onLog,
        });
        return {
          role,
          source,
          sourceFile: fileForImmich,
          sourceUrl: persistedImageUrl,
          ...result,
        };
      }),
    );

    emitImmichLog(
      onLog,
      "info",
      "system",
      "Immich phase 2: Creating persons for student and relatives",
      {
        roles: roleConfigs.map((item) => item.role),
      },
    );

    const personResults = await Promise.all(
      roleConfigs.map(async ({ role, displayName }) => {
        const assetResult = assetResults.find((item) => item.role === role) || {
          status: "skipped",
        };
        const sourceFile = assetResult.sourceFile || null;

        // Skip person creation when there is no image source
        if (!sourceFile) {
          return { role, status: "skipped", personCreateSkipped: true };
        }

        const imageDimensions = await readImageDimensions(sourceFile).catch(
          () => null,
        );
        const result = await syncImmichPersonByUidFromImmichService(
          uidValue,
          role,
          {
            onLog,
            displayName,
            faceAsset:
              assetResult.status === "success" &&
              assetResult.assetId &&
              imageDimensions
                ? {
                    assetId: assetResult.assetId,
                    imageWidth: imageDimensions.imageWidth,
                    imageHeight: imageDimensions.imageHeight,
                  }
                : null,
          },
        );
        return { role, ...result };
      }),
    );

    const buildFinalRoleSync = (role) => {
      const assetResult = assetResults.find((item) => item.role === role) || {
        status: "skipped",
      };
      const personResult = personResults.find((item) => item.role === role) || {
        status: "skipped",
      };

      const status =
        assetResult.status === "failed" || personResult.status === "failed"
          ? "failed"
          : assetResult.status === "success" ||
              personResult.status === "success"
            ? "success"
            : "skipped";

      return {
        role,
        status,
        assetId: assetResult.assetId || "",
        personId: personResult.personId || "",
        personName:
          personResult.personName ||
          buildImmichPersonNameFromImmichService(
            uidValue,
            role,
            roleConfigs.find((item) => item.role === role)?.displayName || "",
          ),
        personCreateSkipped: Boolean(personResult.personCreateSkipped),
        personCreateUrl: personResult.personCreateUrl || "",
        faceCreated: Boolean(personResult.faceCreated),
        faceCreateError: personResult.faceCreateError || "",
        message: assetResult.message || personResult.message || "",
      };
    };

    const immichStudentSync = buildFinalRoleSync("student");
    const immichFatherSync = buildFinalRoleSync("father");
    const immichMotherSync = buildFinalRoleSync("mother");
    const immichGuardianSync = buildFinalRoleSync("guardian");

    return {
      imageUrls,
      immichStudentSync,
      immichFatherSync,
      immichMotherSync,
      immichGuardianSync,
    };
  }

  async function handleSubmit(event) {
    event.preventDefault();

    if (!formData.uid || formData.uid.trim() === "") {
      toast.error("Please enter UID No.");
      return;
    }

    setSaving(true);
    setImmichLogs([]);
    addImmichLog({
      level: "info",
      role: "system",
      message: `Starting student save for UID ${formData.uid.trim()}`,
      details: null,
      timestamp: new Date().toISOString(),
    });

    try {
      const uidValue = formData.uid.trim();
      const {
        imageUrls,
        immichStudentSync,
        immichFatherSync,
        immichMotherSync,
        immichGuardianSync,
      } = await uploadSelectedImages(uidValue, addImmichLog);
      const peopleSyncResults = [
        { role: "student", result: immichStudentSync },
        { role: "father", result: immichFatherSync },
        { role: "mother", result: immichMotherSync },
        { role: "guardian", result: immichGuardianSync },
      ];
      const syncSummary = {
        success: peopleSyncResults.filter(
          (item) => item.result.status === "success",
        ),
        failed: peopleSyncResults.filter(
          (item) => item.result.status === "failed",
        ),
        skipped: peopleSyncResults.filter(
          (item) => item.result.status === "skipped",
        ),
      };
      const immichMetadata = {};

      if (immichStudentSync.status === "success") {
        immichMetadata.immichStudentSyncStatus = "completed";
        immichMetadata.immichStudentSyncedAt = Timestamp.now();
        immichMetadata.immichStudentPersonName =
          immichStudentSync.personName || uidValue;

        if (immichStudentSync.assetId) {
          immichMetadata.immichStudentAssetId = immichStudentSync.assetId;
        }

        if (immichStudentSync.personId) {
          immichMetadata.immichStudentPersonId = immichStudentSync.personId;
        }

        if (immichStudentSync.personCreateUrl) {
          immichMetadata.immichStudentPersonCreateUrl =
            immichStudentSync.personCreateUrl;
        }

        immichMetadata.immichStudentPersonCreateSkipped = Boolean(
          immichStudentSync.personCreateSkipped,
        );
      } else if (immichStudentSync.status === "failed") {
        immichMetadata.immichStudentSyncStatus = "failed";
        immichMetadata.immichStudentSyncError = immichStudentSync.message;
        immichMetadata.immichStudentSyncedAt = Timestamp.now();
      } else if (immichStudentSync.status === "skipped") {
        immichMetadata.immichStudentSyncStatus = "skipped";
      }

      if (immichFatherSync.status === "success") {
        immichMetadata.immichFatherPersonName =
          immichFatherSync.personName || `${uidValue}_father`;
        immichMetadata.immichFatherPersonId = immichFatherSync.personId || "";
        immichMetadata.immichFatherAssetId = immichFatherSync.assetId || "";
        immichMetadata.immichFatherPersonCreateSkipped = Boolean(
          immichFatherSync.personCreateSkipped,
        );
        immichMetadata.immichFatherPersonSyncError = "";
      } else if (immichFatherSync.status === "failed") {
        immichMetadata.immichFatherPersonSyncError = immichFatherSync.message;
      }

      if (immichMotherSync.status === "success") {
        immichMetadata.immichMotherPersonName =
          immichMotherSync.personName || `${uidValue}_mother`;
        immichMetadata.immichMotherPersonId = immichMotherSync.personId || "";
        immichMetadata.immichMotherAssetId = immichMotherSync.assetId || "";
        immichMetadata.immichMotherPersonCreateSkipped = Boolean(
          immichMotherSync.personCreateSkipped,
        );
        immichMetadata.immichMotherPersonSyncError = "";
      } else if (immichMotherSync.status === "failed") {
        immichMetadata.immichMotherPersonSyncError = immichMotherSync.message;
      }

      if (immichGuardianSync.status === "success") {
        immichMetadata.immichGuardianPersonName =
          immichGuardianSync.personName || `${uidValue}_guardian`;
        immichMetadata.immichGuardianPersonId =
          immichGuardianSync.personId || "";
        immichMetadata.immichGuardianAssetId = immichGuardianSync.assetId || "";
        immichMetadata.immichGuardianPersonCreateSkipped = Boolean(
          immichGuardianSync.personCreateSkipped,
        );
        immichMetadata.immichGuardianPersonSyncError = "";
      } else if (immichGuardianSync.status === "failed") {
        immichMetadata.immichGuardianPersonSyncError =
          immichGuardianSync.message;
      }

      if (syncSummary.failed.length === peopleSyncResults.length) {
        immichMetadata.immichPeopleSyncStatus = "failed";
      } else if (syncSummary.failed.length > 0) {
        immichMetadata.immichPeopleSyncStatus = "partial";
      } else if (syncSummary.success.length > 0) {
        immichMetadata.immichPeopleSyncStatus = "completed";
      } else {
        immichMetadata.immichPeopleSyncStatus = "skipped";
      }

      immichMetadata.immichPeopleSyncedAt = Timestamp.now();
      immichMetadata.immichPeopleSyncResults = peopleSyncResults.map(
        ({ role, result }) => ({
          role,
          status: result.status,
          personName: result.personName || "",
          personId: result.personId || "",
          message: result.message || "",
          personCreateSkipped: Boolean(result.personCreateSkipped),
        }),
      );

      addImmichLog({
        level: "info",
        role: "system",
        message: "Immich sync summary",
        details: {
          success: syncSummary.success.length,
          failed: syncSummary.failed.length,
          skipped: syncSummary.skipped.length,
        },
        timestamp: new Date().toISOString(),
      });

      const submissionData = {
        ...formData,
        uid: uidValue,
        activeqr: activateQrOnSubmit ? true : Boolean(formData.activeqr),
      };
      const studentQuery = query(
        collection(db, "students"),
        where("uid", "==", uidValue),
      );
      const snapshot = await getDocs(studentQuery);

      if (!snapshot.empty) {
        const documentReference = snapshot.docs[0].ref;

        await updateDoc(documentReference, {
          ...submissionData,
          ...imageUrls,
          ...immichMetadata,
          updatedAt: Timestamp.now(),
        });

        const successMessage = activateQrOnSubmit
          ? "Student onboarded successfully!"
          : "Student updated successfully!";

        if (syncSummary.failed.length > 0) {
          toast.error("Student saved, but some sync failed.");
        } else {
          toast.success(successMessage);
        }
      } else {
        await addDoc(collection(db, "students"), {
          ...submissionData,
          ...imageUrls,
          ...immichMetadata,
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now(),
        });

        const successMessage = activateQrOnSubmit
          ? "Student onboarded successfully!"
          : "Student added successfully!";

        toast(
          syncSummary.failed.length > 0
            ? `${successMessage}\nFirebase save completed, but Immich sync failed: ${syncSummary.failed.map((item) => `${item.role}: ${item.result.message}`).join(" | ")}`
            : syncSummary.skipped.length > 0
              ? `${successMessage}\nFirebase save completed. Immich sync skipped for: ${syncSummary.skipped.map((item) => item.role).join(", ")}`
              : successMessage,
        );
      }

      addImmichLog({
        level: "success",
        role: "system",
        message: "Firebase save completed",
        details: {
          uid: uidValue,
          immichPeopleSyncStatus: immichMetadata.immichPeopleSyncStatus,
          mode: snapshot.empty ? "create" : "update",
        },
        timestamp: new Date().toISOString(),
      });

      setStudentUid(uidValue);
      setFormData((currentValue) => ({
        ...currentValue,
        uid: uidValue,
        activeqr: activateQrOnSubmit ? true : Boolean(currentValue.activeqr),
      }));
      resetUploads();
    } catch (error) {
      addImmichLog({
        level: "error",
        role: "system",
        message: "Student save failed",
        details: {
          message: error.message,
        },
        timestamp: new Date().toISOString(),
      });
      toast.error("Failed to save student. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  async function handleSearch() {
    const found = await searchStudentByUid(studentUid);

    if (found) {
      toast.success("Student found and details filled!");
      return;
    }

    toast("No student found. You can add new student.", {
      icon: "ℹ️",
    });
  }

  useEffect(() => {
    const auth = getAuth();

    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (!currentUser) {
        setUser(null);
        setCanManageOnboarding(false);
        return;
      }

      setUser(currentUser);

      const userReference = doc(db, "users", currentUser.uid);
      const userSnapshot = await getDoc(userReference);

      if (!userSnapshot.exists()) {
        setCanManageOnboarding(false);
        return;
      }

      const userData = userSnapshot.data();
      setCanManageOnboarding(
        hasPermission(userData, PERMISSIONS.STUDENT_ONBOARDING),
      );
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (initialStudentRecord) {
      const uidValue = String(
        initialStudentRecord.uid || resolvedInitialUid || "",
      ).trim();

      populateStudentForm(initialStudentRecord, uidValue);
      return;
    }

    const normalizedUid = String(resolvedInitialUid || "").trim();

    if (!normalizedUid) {
      setStudentUid("");
      setFormData(buildFreshFormData());
      resetUploads();
      resetPreviews();
      return;
    }

    setStudentUid(normalizedUid);
    searchStudentByUid(normalizedUid);
  }, [
    initialStudentRecord,
    resolvedInitialUid,
    populateStudentForm,
    resetPreviews,
    resetUploads,
    searchStudentByUid,
  ]);

  if (!user) {
    return (
      <div
        className={
          embedded
            ? "flex items-center justify-center rounded-2xl bg-gradient-to-br from-blue-50 to-blue-100 p-6"
            : "flex min-h-screen items-center justify-center bg-gradient-to-br from-blue-50 to-blue-100"
        }
      >
        <div className="rounded-xl bg-white p-8 text-center shadow-lg">
          <h2 className="mb-4 text-2xl font-bold text-blue-700">
            Student Onboarding
          </h2>
          <p className="mb-2 text-gray-600">
            Please login to access this form.
          </p>
        </div>
      </div>
    );
  }

  if (!canManageOnboarding) {
    const allowedRoles = getAllowedRoles(PERMISSIONS.STUDENT_ONBOARDING).join(
      ", ",
    );

    return (
      <div
        className={
          embedded
            ? "flex items-center justify-center rounded-2xl bg-gradient-to-br from-blue-50 to-blue-100 p-6"
            : "flex min-h-screen items-center justify-center bg-gradient-to-br from-blue-50 to-blue-100"
        }
      >
        <div className="rounded-xl bg-white p-8 text-center shadow-lg">
          <h2 className="mb-4 text-2xl font-bold text-blue-700">
            Student Onboarding
          </h2>
          <p className="mb-2 text-gray-600">
            Allowed roles for this form: {allowedRoles || "admin"}.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className={
        embedded
          ? "mt-6"
          : "flex min-h-screen items-center justify-center bg-gradient-to-br from-blue-50 to-blue-100 p-4"
      }
    >
      <form
        onSubmit={handleSubmit}
        className="flex w-full max-w-3xl flex-col gap-8 rounded-2xl bg-white p-8 shadow-2xl"
      >
        <section>
          <h3 className="mb-4 border-b-2 border-blue-100 pb-1 text-xl font-semibold text-blue-600">
            Student Information
          </h3>

          <div className="mb-6 rounded-2xl border border-emerald-100 bg-emerald-50/80 p-4 shadow-sm">
            <p className="text-sm font-semibold uppercase tracking-[0.18em] text-emerald-700">
              Search Student
            </p>
            {/* <p className="mt-1 text-sm text-gray-600">
              Use the student UID to load and update an existing record. If
              nothing is found, continue below to add a new admission.
            </p> */}

            <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center">
              <input
                className="input-field-thick flex-1"
                type="text"
                name="uid"
                placeholder="Enter UID No. to search"
                value={studentUid}
                onChange={handleUidInputChange}
              />
              <button
                type="button"
                onClick={handleSearch}
                disabled={searching}
                className="rounded-lg bg-green-600 px-4 py-2 text-white transition hover:bg-green-700 disabled:cursor-not-allowed disabled:bg-green-400"
              >
                {searching ? "Searching..." : "Search UID"}
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <div>
              <label className="mb-1 block font-semibold text-blue-700">
                Student Photo
              </label>
              <div className="flex items-center gap-4">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleStudentImageChange}
                  className="input-field-thick"
                />
                {studentPreview && (
                  <img
                    src={studentPreview}
                    alt="Student Preview"
                    className="h-16 w-16 rounded-full border-2 border-blue-300 object-cover"
                  />
                )}
              </div>
            </div>

            <input
              className="input-field-thick"
              type="text"
              name="name"
              placeholder="Student Name"
              value={formData.name}
              onChange={handleChange}
            />

            <select
              className="input-field-thick"
              name="className"
              value={formData.className}
              onChange={handleChange}
            >
              <option value="">Select Class</option>
              {CLASS_OPTIONS.map((item) => (
                <option key={item} value={item}>
                  {item}
                </option>
              ))}
            </select>

            <input
              className="input-field-thick"
              type="text"
              name="rollNo"
              placeholder="Roll No."
              value={formData.rollNo}
              onChange={handleChange}
            />

            <input
              className="input-field-thick cursor-not-allowed bg-gray-100"
              type="text"
              name="uid"
              placeholder="UID No"
              value={formData.uid}
              tabIndex={-1}
              readOnly
              onFocus={(event) => event.target.blur()}
            />

            <div>
              <label className="mb-1 block font-semibold text-blue-700">
                Date Of Birth
              </label>
              <input
                className="input-field-thick"
                type="date"
                name="dob"
                value={formData.dob}
                onChange={handleDateChange}
                placeholder="dd/mm/yyyy"
              />
            </div>

            <div>
              <label className="mb-1 block font-semibold text-blue-700">
                Admission Date
              </label>
              <input
                className="input-field-thick"
                type="date"
                name="admissionDate"
                value={formData.admissionDate}
                onChange={handleDateChange}
                placeholder="dd/mm/yyyy"
              />
            </div>

            <input
              className="input-field-thick"
              type="text"
              name="studentAadhaar"
              placeholder="Student Aadhaar"
              value={formData.studentAadhaar}
              onChange={handleChange}
            />

            <div className="sm:col-span-2">
              <label className="mb-1 block font-semibold text-blue-700">
                QR Image URL
              </label>
              <input
                className="input-field-thick"
                type="url"
                name="qrImageUrl"
                placeholder="https://..."
                value={formData.qrImageUrl}
                onChange={handleChange}
              />

              {formData.qrImageUrl ? (
                <img
                  src={formData.qrImageUrl}
                  alt="QR Preview"
                  className="mt-3 h-24 w-24 rounded-xl border border-blue-200 bg-white p-2 object-contain"
                  onError={(event) => {
                    event.currentTarget.style.display = "none";
                  }}
                />
              ) : null}
            </div>

            <label className="flex items-center gap-3 rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm font-semibold text-blue-800">
              <input
                type="checkbox"
                name="activeqr"
                checked={Boolean(formData.activeqr)}
                disabled
                className="h-4 w-4 cursor-not-allowed"
              />
              Active QR
            </label>

            {activateQrOnSubmit ? (
              <p className="sm:col-span-2 -mt-2 text-sm text-emerald-700">
                This will stay inactive until you click the onboarding button
                below.
              </p>
            ) : null}
          </div>
        </section>

        <section>
          <h3 className="mb-4 border-b-2 border-blue-100 pb-1 text-xl font-semibold text-blue-600">
            Parent Information
          </h3>

          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <div>
              <label className="mb-1 block font-semibold text-blue-700">
                Father Photo
              </label>
              <div className="flex items-center gap-4">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFatherImageChange}
                  className="input-field-thick"
                />
                {fatherPreview && (
                  <img
                    src={fatherPreview}
                    alt="Father Preview"
                    className="h-16 w-16 rounded-full border-2 border-blue-300 object-cover"
                  />
                )}
              </div>
            </div>

            <input
              className="input-field-thick"
              type="text"
              name="father"
              placeholder="Father Name"
              value={formData.father}
              onChange={handleChange}
            />

            <input
              className="input-field-thick"
              type="text"
              name="fatherAadhaar"
              placeholder="Father Aadhaar"
              value={formData.fatherAadhaar}
              onChange={handleChange}
            />

            <div>
              <label className="mb-1 block font-semibold text-blue-700">
                Mother Photo
              </label>
              <div className="flex items-center gap-4">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleMotherImageChange}
                  className="input-field-thick"
                />
                {motherPreview && (
                  <img
                    src={motherPreview}
                    alt="Mother Preview"
                    className="h-16 w-16 rounded-full border-2 border-blue-300 object-cover"
                  />
                )}
              </div>
            </div>

            <input
              className="input-field-thick"
              type="text"
              name="mother"
              placeholder="Mother Name"
              value={formData.mother}
              onChange={handleChange}
            />

            <input
              className="input-field-thick"
              type="text"
              name="motherAadhaar"
              placeholder="Mother Aadhaar"
              value={formData.motherAadhaar}
              onChange={handleChange}
            />

            <input
              className="input-field-thick"
              type="text"
              name="phone"
              placeholder="Phone No."
              value={formData.phone}
              onChange={handleChange}
            />

            <input
              className="input-field-thick"
              type="text"
              name="altPhone"
              placeholder="Alt Phone No."
              value={formData.altPhone}
              onChange={handleChange}
            />

            <textarea
              className="input-field-thick col-span-1 sm:col-span-2"
              rows="2"
              name="address"
              placeholder="Address"
              value={formData.address}
              onChange={handleChange}
            />
          </div>
        </section>

        <section>
          <h3 className="mb-4 border-b-2 border-blue-100 pb-1 text-xl font-semibold text-blue-600">
            Transportation Details
          </h3>

          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <select
              className="input-field-thick"
              name="transportation"
              value={formData.transportation}
              onChange={handleChange}
            >
              <option value="Self">Self</option>
              <option value="Provided">School Provided</option>
            </select>

            <input
              className="input-field-thick"
              type="text"
              name="busRouteNo"
              placeholder="Bus Route No."
              value={formData.busRouteNo}
              onChange={handleChange}
            />

            <input
              className="input-field-thick"
              type="text"
              name="busNo"
              placeholder="Bus No."
              value={formData.busNo}
              onChange={handleChange}
            />

            <input
              className="input-field-thick"
              type="text"
              name="driverName"
              placeholder="Driver Name"
              value={formData.driverName}
              onChange={handleChange}
            />

            <input
              className="input-field-thick"
              type="text"
              name="conductorName"
              placeholder="Conductor Name"
              value={formData.conductorName}
              onChange={handleChange}
            />

            <input
              className="input-field-thick"
              type="text"
              name="busPhone"
              placeholder="Bus Phone (Driver/Conductor)"
              value={formData.busPhone}
              onChange={handleChange}
            />
          </div>
        </section>

        <section>
          <h3 className="mb-4 border-b-2 border-blue-100 pb-1 text-xl font-semibold text-blue-600">
            Guardian Information
          </h3>

          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <div>
              <label className="mb-1 block font-semibold text-blue-700">
                Guardian Photo
              </label>
              <div className="flex items-center gap-4">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleGuardianImageChange}
                  className="input-field-thick"
                />
                {guardianPreview && (
                  <img
                    src={guardianPreview}
                    alt="Guardian Preview"
                    className="h-16 w-16 rounded-full border-2 border-blue-300 object-cover"
                  />
                )}
              </div>
            </div>

            <input
              className="input-field-thick"
              type="text"
              name="guardianName"
              placeholder="Guardian Name"
              value={formData.guardianName}
              onChange={handleChange}
            />

            <input
              className="input-field-thick"
              type="text"
              name="guardianRelation"
              placeholder="Relationship with Guardian"
              value={formData.guardianRelation}
              onChange={handleChange}
            />

            <input
              className="input-field-thick"
              type="text"
              name="guardianPhone"
              placeholder="Guardian Phone"
              value={formData.guardianPhone}
              onChange={handleChange}
            />

            <textarea
              className="input-field-thick col-span-1 sm:col-span-2"
              rows="2"
              name="guardianAddress"
              placeholder="Guardian Address"
              value={formData.guardianAddress}
              onChange={handleChange}
            />
          </div>
        </section>

        {/* <section className="rounded-2xl border border-indigo-200 bg-indigo-50/60 p-4">
          <div className="flex items-center justify-between gap-2">
            <h3 className="text-sm font-semibold uppercase tracking-[0.18em] text-indigo-800">
              Immich Background Logs
            </h3>
            <button
              type="button"
              onClick={() => setImmichLogs([])}
              className="rounded-md border border-indigo-200 bg-white px-2 py-1 text-xs font-semibold text-indigo-700 transition hover:bg-indigo-100"
            >
              Clear
            </button>
          </div>

          {immichLogs.length === 0 ? (
            <p className="mt-3 text-sm text-slate-600">
              No logs yet. Save student details to view background Immich
              request and response logs.
            </p>
          ) : (
            <div className="mt-3 max-h-72 space-y-2 overflow-auto pr-1">
              {immichLogs.map((logItem) => {
                const levelClass =
                  IMMICH_LOG_LEVEL_STYLES[logItem.level] ||
                  IMMICH_LOG_LEVEL_STYLES.info;
                const timeLabel = new Date(
                  logItem.timestamp || Date.now(),
                ).toLocaleTimeString();

                return (
                  <div
                    key={logItem.id}
                    className={`rounded-xl border px-3 py-2 ${levelClass}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-xs font-semibold uppercase tracking-[0.12em]">
                        {logItem.role || "system"} | {logItem.level}
                      </p>
                      <p className="text-[11px] font-medium opacity-80">
                        {timeLabel}
                      </p>
                    </div>
                    <p className="mt-1 text-sm font-medium">
                      {logItem.message}
                    </p>
                    {logItem.details ? (
                      <pre className="mt-2 overflow-x-auto whitespace-pre-wrap rounded-lg bg-white/80 p-2 text-[11px] leading-4 text-slate-700">
                        {JSON.stringify(logItem.details, null, 2)}
                      </pre>
                    ) : null}
                  </div>
                );
              })}
            </div>
          )}
        </section> */}

        <button
          type="submit"
          disabled={saving}
          className={`w-full rounded-xl bg-blue-600 px-6 py-3 text-lg font-semibold text-white shadow transition duration-200 hover:bg-blue-700 ${
            saving ? "cursor-not-allowed opacity-60" : ""
          }`}
        >
          {saving ? "Saving..." : "Save Student Details"}
        </button>
      </form>

      <style>
        {`
          .input-field-thick {
            width: 100%;
            padding: 0.75rem 1rem;
            border-radius: 0.75rem;
            border: 1px solid #60a5fa;
            outline: none;
            font-size: 1.1rem;
            background: #f8fafc;
            transition: border-color 0.2s, box-shadow 0.2s;
            margin-bottom: 0.1rem;
          }

          .input-field-thick:focus {
            border-color: #2563eb;
            box-shadow: 0 0 0 2px #93c5fd;
            background: #fff;
          }
        `}
      </style>
    </div>
  );
}
